import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertTriangle, X, RefreshCw } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, BarChart, Bar, Area, AreaChart
} from "recharts";
import IndiaSalesHeatmap from "@/components/IndiaSalesHeatmap";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import QuotationTypeToggle from "@/components/QuotationTypeToggle";
import { useQuotationTypeView, matchesQuotationType } from "@/hooks/useQuotationTypeView";

const BRAND_COLORS = { Wellcore: "#14b8a6", Dynamite: "#f97316", Okami: "#8b5cf6", YouWeFit: "#3b82f6", "Zero Sugar": "#ec4899", OSMO: "#10b981", Unknown: "#94a3b8" };
const PIE_COLORS = ["#14b8a6", "#f97316", "#8b5cf6", "#3b82f6", "#ec4899", "#10b981", "#94a3b8"];
const ALL_BRANDS = ["Wellcore", "Dynamite", "Okami", "YouWeFit", "Zero Sugar", "OSMO"];
const formatCurrency = (v) => { if (v >= 1e7) return `₹${(v/1e7).toFixed(2)} Cr`; if (v >= 1e5) return `₹${(v/1e5).toFixed(2)} L`; if (v >= 1e3) return `₹${(v/1e3).toFixed(1)} K`; return `₹${v.toLocaleString("en-IN")}`; };

export default function RevenueAnalytics() {
  const [allQuotations, setAllQuotations] = useState([]);
  const [allSellers, setAllSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`;
  const today = now.toISOString().substring(0,10);
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(today);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [brandFilterOpen, setBrandFilterOpen] = useState(false);
  const [timeGranularity, setTimeGranularity] = useState("day");
  const [typeView] = useQuotationTypeView();

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    setLoading(true);
    try {
      const [qRes, sRes] = await Promise.all([
        axios.get(`${API}/quotations`, { withCredentials: true }),
        axios.get(`${API}/sellers`, { withCredentials: true })
      ]);
      setAllQuotations(qRes.data || []);
      setAllSellers(sRes.data || []);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  const sellerMap = useMemo(() => { const m = {}; allSellers.forEach(s => { m[s.id] = s; }); return m; }, [allSellers]);
  const toggleBrand = (b) => setSelectedBrands(p => p.includes(b) ? p.filter(x=>x!==b) : [...p, b]);
  const clearFilters = () => { setDateFrom(monthStart); setDateTo(today); setSelectedBrands([]); };
  const hasActiveFilters = dateFrom || dateTo || selectedBrands.length > 0;

  const filteredQuotations = useMemo(() => {
    let f = [...allQuotations];
    // Analytics-wide rule: only invoiced quotations are counted, filtered by invoice_date.
    f = f.filter(q => !!q.invoice_date);
    if (typeView !== "all") f = f.filter(q => matchesQuotationType(q, typeView));
    if (dateFrom) f = f.filter(q => (q.invoice_date?.substring(0,10)||"") >= dateFrom);
    if (dateTo) f = f.filter(q => (q.invoice_date?.substring(0,10)||"") <= dateTo);
    if (selectedBrands.length > 0) f = f.filter(q => (q.items||[]).some(i => selectedBrands.includes(i.brand)));
    return f;
  }, [allQuotations, dateFrom, dateTo, selectedBrands, typeView]);

  const revenueByDate = useMemo(() => {
    const m = {};
    filteredQuotations.forEach(q => { const d = q.invoice_date?.substring(0,10); if(d) m[d] = (m[d]||0)+(q.total_invoice_value||0); });
    return Object.entries(m).sort(([a],[b])=>a.localeCompare(b)).map(([d,v])=>({date:d,value:v,label:new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"short"})}));
  }, [filteredQuotations]);

  const aovByDate = useMemo(() => {
    const m = {};
    filteredQuotations.forEach(q => { const d = q.invoice_date?.substring(0,10); if(!d) return; if(!m[d]) m[d]={sum:0,cnt:0}; m[d].sum+=q.total_invoice_value||0; m[d].cnt++; });
    return Object.entries(m).sort(([a],[b])=>a.localeCompare(b)).map(([d,v])=>({date:d,value:v.cnt>0?v.sum/v.cnt:0,label:new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"short"})}));
  }, [filteredQuotations]);

  const topSellers = useMemo(() => {
    const m = {};
    filteredQuotations.forEach(q => { const name = (sellerMap[q.seller_id]?.seller_name || q.seller_name || "Unknown").trim(); if(!m[name]) m[name]={name,revenue:0,count:0}; m[name].revenue+=q.total_invoice_value||0; m[name].count++; });
    const sorted = Object.values(m).sort((a,b)=>b.revenue-a.revenue).slice(0,10);
    const total = sorted.reduce((s,r)=>s+r.revenue,0);
    return sorted.map(r => ({...r, contribution: total>0 ? (r.revenue/total*100) : 0}));
  }, [filteredQuotations, sellerMap]);

  const SellerYAxisTick = ({ x, y, payload }) => {
    const full = String(payload?.value ?? "");
    const label = full.length > 18 ? `${full.slice(0, 17)}…` : full;
    return (
      <text x={x} y={y} dy={3} textAnchor="end" fontSize={11} fill="#475569">
        <title>{full}</title>
        {label}
      </text>
    );
  };

  const SellerBarTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
      <div data-testid="seller-bar-tooltip" className="bg-white border border-slate-200 rounded-lg shadow-md px-3 py-2 text-xs">
        <div className="font-semibold text-slate-900 mb-1">{d.name}</div>
        <div className="text-slate-600">Revenue: <span className="font-mono text-slate-900">{formatCurrency(d.revenue)}</span></div>
        <div className="text-slate-600">Contribution: <span className="font-mono text-slate-900">{d.contribution.toFixed(1)}%</span></div>
      </div>
    );
  };

  const brandData = useMemo(() => {
    const bs = {};
    filteredQuotations.forEach(q => (q.items||[]).forEach(i => { const b=i.brand||"Unknown"; if(!bs[b]) bs[b]=0; bs[b]+=i.invoice_value||0; }));
    const total = Object.values(bs).reduce((s,v)=>s+v,0);
    return Object.entries(bs).map(([n,v])=>({name:n,value:v,contribution: total>0 ? (v/total*100) : 0})).sort((a,b)=>b.value-a.value);
  }, [filteredQuotations]);

  const BrandPieTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
      <div data-testid="brand-pie-tooltip" className="bg-white border border-slate-200 rounded-lg shadow-md px-3 py-2 text-xs">
        <div className="font-semibold text-slate-900 mb-1">{d.name}</div>
        <div className="text-slate-600">Revenue: <span className="font-mono text-slate-900">{formatCurrency(d.value)}</span></div>
        <div className="text-slate-600">Contribution: <span className="font-mono text-slate-900">{d.contribution.toFixed(1)}%</span></div>
      </div>
    );
  };

  const FilterCheckItem = ({ checked, label, onClick }) => (
    <div className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-100 ${checked ? 'bg-accent/10' : ''}`} onClick={onClick}>
      <div className={`w-4 h-4 rounded border ${checked ? 'bg-accent border-accent' : 'border-slate-300'}`}>{checked && <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}</div>
      <span className="text-sm">{label}</span>
    </div>
  );

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" /></div>;

  return (
    <div className="space-y-5">
      <div className="bg-white pb-4 mb-1 border-b border-[#E8E8E8]">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-xl font-semibold text-[#0F0F0F] mr-auto">Revenue Analytics</h1>
          <DateRangeFilter from={dateFrom} to={dateTo} onChange={(f,t)=>{ setDateFrom(f); setDateTo(t); }} testId="revenue-date-range" />
          <QuotationTypeToggle testId="revenue-qtype" />
          <Popover open={brandFilterOpen} onOpenChange={setBrandFilterOpen}>
            <PopoverTrigger asChild><Button variant="outline" className="h-8 text-xs px-3 border-[#E8E8E8]">{selectedBrands.length===0?"All Brands":`${selectedBrands.length} Brand${selectedBrands.length>1?"s":""}`}</Button></PopoverTrigger>
            <PopoverContent className="w-56 p-2"><div className="space-y-1">{ALL_BRANDS.map(b=><FilterCheckItem key={b} checked={selectedBrands.includes(b)} label={b} onClick={()=>toggleBrand(b)} />)}</div></PopoverContent>
          </Popover>
          {hasActiveFilters && <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 px-2 text-xs text-red-500 hover:bg-red-50"><X className="h-3 w-3 mr-1" />Clear</Button>}
          <Button variant="outline" size="sm" onClick={fetchData} className="h-8 px-2 border-[#E8E8E8]"><RefreshCw className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><div><CardTitle className="text-base font-semibold">Revenue Over Time</CardTitle></div><Select value={timeGranularity} onValueChange={setTimeGranularity}><SelectTrigger className="w-24 h-7 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="day">Daily</SelectItem><SelectItem value="week">Weekly</SelectItem><SelectItem value="month">Monthly</SelectItem></SelectContent></Select></CardHeader>
        <CardContent><div className="h-[280px]"><ResponsiveContainer width="100%" height="100%"><AreaChart data={revenueByDate} margin={{top:5,right:20,left:0,bottom:5}}><defs><linearGradient id="rg" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#1D9E75" stopOpacity={0.15}/><stop offset="95%" stopColor="#1D9E75" stopOpacity={0}/></linearGradient></defs><CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8"/><XAxis dataKey="label" tick={{fontSize:10}} tickLine={false} axisLine={{stroke:"#E8E8E8"}} interval={revenueByDate.length>15?Math.floor(revenueByDate.length/8):0}/><YAxis tickFormatter={formatCurrency} tick={{fontSize:10}} tickLine={false} axisLine={false} width={70}/><Tooltip formatter={v=>[`₹${v.toLocaleString("en-IN")}`,"Revenue"]} contentStyle={{borderRadius:8,border:"1px solid #E8E8E8",fontSize:12}}/><Area type="monotone" dataKey="value" stroke="#1D9E75" strokeWidth={2} fill="url(#rg)"/></AreaChart></ResponsiveContainer></div></CardContent>
      </Card>

      <Card><CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Avg. Order Value Trend</CardTitle></CardHeader>
        <CardContent><div className="h-[240px]"><ResponsiveContainer width="100%" height="100%"><LineChart data={aovByDate} margin={{top:5,right:20,left:0,bottom:5}}><CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8"/><XAxis dataKey="label" tick={{fontSize:10}} tickLine={false} axisLine={{stroke:"#E8E8E8"}} interval={aovByDate.length>15?Math.floor(aovByDate.length/8):0}/><YAxis tickFormatter={formatCurrency} tick={{fontSize:10}} tickLine={false} axisLine={false} width={70}/><Tooltip formatter={v=>[`₹${v.toLocaleString("en-IN")}`,"AOV"]} contentStyle={{borderRadius:8,border:"1px solid #E8E8E8",fontSize:12}}/><Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} dot={false}/></LineChart></ResponsiveContainer></div></CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card><CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Revenue by Seller (Top 10)</CardTitle></CardHeader>
          <CardContent><div className="h-[300px]"><ResponsiveContainer width="100%" height="100%"><BarChart data={topSellers} layout="vertical" margin={{top:5,right:20,left:0,bottom:5}}><CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" horizontal={false}/><XAxis type="number" tickFormatter={formatCurrency} tick={{fontSize:10}} tickLine={false} axisLine={{stroke:"#E8E8E8"}}/><YAxis type="category" dataKey="name" width={140} interval={0} tick={<SellerYAxisTick />} tickLine={false} axisLine={false}/><Tooltip content={<SellerBarTooltip />} cursor={{fill:"#f1f5f9"}} contentStyle={{borderRadius:8,border:"1px solid #E8E8E8",fontSize:12}}/><Bar dataKey="revenue" fill="#1D9E75" radius={[0,4,4,0]} barSize={18}/></BarChart></ResponsiveContainer></div></CardContent>
        </Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Revenue by Brand</CardTitle></CardHeader>
          <CardContent><div className="h-[300px]"><ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={brandData} cx="50%" cy="50%" innerRadius={60} outerRadius={95} paddingAngle={2} dataKey="value">{brandData.map((e,i)=><Cell key={i} fill={BRAND_COLORS[e.name]||PIE_COLORS[i%PIE_COLORS.length]}/>)}</Pie><Tooltip content={<BrandPieTooltip />} contentStyle={{borderRadius:8,fontSize:12}}/><Legend formatter={v=><span className="text-xs">{v}</span>}/></PieChart></ResponsiveContainer></div></CardContent>
        </Card>
      </div>

      <IndiaSalesHeatmap quotations={filteredQuotations} sellerMap={sellerMap} />
    </div>
  );
}
